import React, { useState, useEffect, Fragment, useCallback } from 'react';
import { Row, Col, Button, Tab, Tabs, Form, OverlayTrigger, Tooltip /*, Spinner*/ } from 'react-bootstrap';
import {
	getProjectFrameworks,
	updateFrameworkValues,
	createProjectFrameworks,
	getAllStreams,
	addMonthEndToFramework,
	addMonthStartToFramework,
	deleteMonthStartToFramework,
	deleteMonthEndToFramework,
	deleteVariant,
} from '@lib/api';
import UploadDocument from '@components/form/upload/uploadDocuments';
import StatusBar from '@components/StatusBar';
import { InputNumber, Message } from 'primereact';
import { formatToDecimals } from '@lib/helpers/formatting';
import { months } from '@lib/helpers/calendar';
import { IcoPlus, IcoMinusCircled } from '@icons';
import DataHeader from 'components/DataHeader';
import { OETrans } from 'components/translation/OETrans';
import { translate } from 'components/translation/translation';
import { DialogConfirmation } from 'components/atomic/molecules/index';
import { Loading } from '@components/atomic/atoms';

export const SwitchShowMonths = ({
	filteredValues,
	months,
	handleTargetAmountChange,
	frameworkIndex,
	localLang,
	totalPerMonths,
	isActive,
	isEnabledToView,
}) => {
	const [showMonths, setShowMonths] = useState(false);

	return (
		<div className="py-3 ps-3">
			<Row>
				<Col className="">
					<Row className="my-3">
						<Col className="col-auto me-4 fw-bold">Totale</Col>
						<Col className="text-end">{formatToDecimals(totalPerMonths) + ' €'}</Col>
					</Row>
				</Col>
				<Col id="col-space-planning">&nbsp;</Col>
				<Col className="col-auto ms-auto">
					<Form.Check
						className="my-3"
						inline
						label={'Visualizza mesi'}
						name="group1"
						type="switch"
						value="showMonths"
						defaultChecked={showMonths}
						onClick={(e) => setShowMonths(e.target.checked)}
					/>
				</Col>
			</Row>

			{showMonths === true ? (
				<Row className="overflow-auto">
					{filteredValues
						.sort((valueA, valueB) => valueA.month - valueB.month)
						.map((econValue, valueIndex) => {
							//console.log('econValue', econValue);
							return (
								<Col key={'econValue' + econValue.id} className="pt-3 pb-4 col-month">
									<Row className="flex-row">
										<Form.Label className={`fw-normal ${!isEnabledToView && 'text-end'}`}>{months[econValue.month - 1]}</Form.Label>
										{isEnabledToView ? (
											<InputNumber
												name="value"
												onChange={(e) => handleTargetAmountChange(frameworkIndex, econValue.id, 'value', e.value)}
												min={0}
												//minFractionDigits={0}
												//maxFractionDigits={2}
												//suffix={'€'}
												mode="currency"
												currency="EUR"
												locale={localLang}
												className="md"
												value={econValue.value}
												disabled={!isActive}
											/>
										) : (
											<Form.Text as="p" className="text-end">{`${formatToDecimals(econValue.value)} €`}</Form.Text>
										)}
									</Row>
									{/* //only for check */}
									{/* <Row style={{fontSize: '13px'}}>
                     <span>id:{econValue.id}</span>
                     <span>economicFrameworkId:{econValue.projectEconomicFrameworkId}</span><br/>
                     <span>expenseItemId:{econValue.projectExpenseItemId}</span><br/>
                     <span>streamId:{econValue.projectStreamId}</span><br/>
                     <span>year:{econValue.year}</span>
                  </Row> */}
								</Col>
							);
						})}
				</Row>
			) : null}
		</div>
	);
};

const BudgetStreams = ({ thisProject, projectNoticeExpenseItems, isEnabledToView, projectExpenseItems }) => {
	const [projectFrameworks, setProjectFrameworks] = useState(null);
	const [projectExpenseItemsForAmounts, setProjectExpenseItemsForAmounts] = useState([]);
	const [projectFrameworksAmount, setProjectFrameworksAmount] = useState([]);
	const [streams, setStreams] = useState([]);
	const [activeKeyFramework, setActiveKeyFramework] = useState('');
	const localLang = `${localStorage.getItem('userLanguage').toLowerCase()}-${localStorage.getItem('userLanguage')}`;
	const [isThereStreams, setIsThereStreams] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [sbShow, setSbShow] = useState(<></>);
	//console.log('projectFrameworks', projectFrameworks);
	//console.log('projectExpenseItemsForAmounts', projectExpenseItemsForAmounts);

	const handleStatusBar = (status, message, timeout) => {
		setSbShow(<StatusBar status={status} message={message} timeout={timeout} />);
	};

	const refreshProjectFrameworks = useCallback(() => {
		getProjectFrameworks(thisProject.id).then((frameworks) => {
			setProjectFrameworks(
				frameworks.data.map((framework) => ({
					id: framework.id,
					active: framework.active,
					economicFrameworkValues: framework.economicFrameworkValues,
					number: framework.number,
				})),
			);

			if (frameworks.data.length > 0) {
				setActiveKeyFramework(`framework-${frameworks.data.sort((a, b) => b.id - a.id)[0]?.id}`);
			}
		});
	}, [thisProject.id]);

	useEffect(() => {
		if (thisProject.id) {
			refreshProjectFrameworks();

			getAllStreams(thisProject.id).then((retrievedStreams) => {
				if (retrievedStreams.success) {
					setStreams(
						retrievedStreams.data.map((stream) => ({
							id: stream.id,
							streamId: stream.streamId,
							name: stream.name,
						})),
					);

					setIsThereStreams(retrievedStreams.data.length > 0);
				}
			});
		}
	}, [refreshProjectFrameworks, thisProject]);

	useEffect(() => {
		setProjectExpenseItemsForAmounts(projectExpenseItems);
	}, [projectExpenseItems]);

	const updateValues = async (projectEconomicFrameworkId) => {
		setSbShow(<></>);

		const arrayForUpdate = projectFrameworksAmount.map((projectFrameworkAmount) => ({
			id: projectFrameworkAmount.id,
			value: projectFrameworkAmount.value,
		}));
		//console.log('arrayForUpdate', arrayForUpdate);

		const result = await updateFrameworkValues(thisProject.id, projectEconomicFrameworkId, arrayForUpdate);
		//console.log('result', result);

		if (result.success) {
			handleStatusBar('success', translate('common.message.edited', 'Elemento modificato con successo'), true);

			setTimeout(() => {
				getProjectFrameworks(thisProject.id).then((frameworks) => {
					setProjectFrameworks(
						frameworks.data.map((framework) => ({
							id: framework.id,
							active: framework.active,
							economicFrameworkValues: framework.economicFrameworkValues,
							number: framework.number,
						})),
					);
				});
			}, 2000);
		} else {
			handleStatusBar(
				'error',
				result.message ?? translate('common.error.save', 'Si è verificato un errore durante il salvataggio delle informazioni'),
				false,
			);
		}
	};

	const handleTargetAmountChange = (frameworkIndex, index, type, value) => {
		const updatedTargetsAmount = [...projectFrameworks[frameworkIndex].economicFrameworkValues];

		var array_index = updatedTargetsAmount.findIndex((item) => item.id === index);

		updatedTargetsAmount[array_index][type] = value;

		setProjectFrameworksAmount(updatedTargetsAmount);
	};

	const createFramework = async () => {
		const result = await createProjectFrameworks({ projectId: thisProject.id, active: 1 });

		if (result.success) {
			handleStatusBar('success', `Quadro economico generato con successo`, true);

			let updatedFrameworks = [...projectFrameworks];
			updatedFrameworks = updatedFrameworks.map((framework) => ({ ...framework, active: false }));
			updatedFrameworks.push(result.data.pop());

			setProjectFrameworks(updatedFrameworks);

			setActiveKeyFramework(`framework-${updatedFrameworks.sort((a, b) => b.id - a.id)[0]?.id}`);
		} else {
			handleStatusBar('error', `Non è stato possibile generare il quadro economico`, false);
		}
	};

	const AmountsTab = () => {
		const handleDeleteVariant = () => {
			setShowDeleteModal(true);
		};

		const removeVariant = async () => {
			const frameworkActiveId = projectFrameworks.find((framework) => framework.active).id;

			const result = await deleteVariant(thisProject.id, frameworkActiveId);
			if (result.success) {
				setShowDeleteModal(false);

				handleStatusBar('success', `Quadro economico eliminato con successo`, false);

				refreshProjectFrameworks();
			} else {
				handleStatusBar('error', `Non è stato possibile eliminare il quadro economico`, false);
			}
		};

		const addMonthEnd = async (index, frameworkId) => {
			const result = await addMonthEndToFramework(thisProject.id, frameworkId);
			if (result.success) {
				const updatedProjectFrameworks = [...projectFrameworks];
				updatedProjectFrameworks[index].economicFrameworkValues = result.data.economicFrameworkValues;
				setProjectFrameworks(updatedProjectFrameworks);
			} else {
				handleStatusBar('error', `Non è stato possibile aggiungere un mese al quadro economico`, false);
			}
		};

		const addMonthStart = async (index, frameworkId) => {
			const result = await addMonthStartToFramework(thisProject.id, frameworkId);
			if (result.success) {
				const updatedProjectFrameworks = [...projectFrameworks];
				updatedProjectFrameworks[index].economicFrameworkValues = result.data.economicFrameworkValues;
				setProjectFrameworks(updatedProjectFrameworks);
			} else {
				handleStatusBar('error', `Non è stato possibile aggiungere un mese al quadro economico`, false);
			}
		};

		const deleteMonthStart = async (index, frameworkId) => {
			const result = await deleteMonthStartToFramework(thisProject.id, frameworkId);
			if (result.success) {
				const updatedProjectFrameworks = [...projectFrameworks];
				updatedProjectFrameworks[index].economicFrameworkValues = result.data.economicFrameworkValues;
				setProjectFrameworks(updatedProjectFrameworks);
			} else {
				handleStatusBar('error', `Non è stato possibile eliminare un mese dal quadro economico`, false);
			}
		};

		const deleteMonthEnd = async (index, frameworkId) => {
			const result = await deleteMonthEndToFramework(thisProject.id, frameworkId);
			if (result.success) {
				const updatedProjectFrameworks = [...projectFrameworks];
				updatedProjectFrameworks[index].economicFrameworkValues = result.data.economicFrameworkValues;
				setProjectFrameworks(updatedProjectFrameworks);
			} else {
				handleStatusBar('error', `Non è stato possibile eliminare un mese dal quadro economico`, false);
			}
		};

		const ConditionalWrapper = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children);

		return (
			<Fragment>
				{projectFrameworks && projectFrameworks.length === 0 ? (
					<>
						<Message
							severity="warn"
							text="Non hai ancora creato un quadro economico. Dopo aver creato il quadro economico non sarà possibile modificare streams e sotto voci di spesa"
						/>

						{isEnabledToView && (
							<Row className="mt-5 mb-4">
								<Col className="text-center">
									<ConditionalWrapper
										condition={!isThereStreams}
										wrapper={(children) => (
											<OverlayTrigger placement="top" overlay={<Tooltip>Non sono stati ancora creati degli stream sul progetto</Tooltip>}>
												{children}
											</OverlayTrigger>
										)}
									>
										<span className="d-inline-block" style={{ margin: '4rem auto' }}>
											<Button onClick={createFramework} disabled={!isThereStreams}>
												Crea nuovo quadro economico
											</Button>
										</span>
									</ConditionalWrapper>
								</Col>
							</Row>
						)}
					</>
				) : (
					<>
						<div className="simple-box scenaritab mt-1">
							<Row>
								<Tabs
									id="controlled-tab-framework"
									activeKey={activeKeyFramework}
									onSelect={(k) => {
										k === 'add-target' ? createFramework() : setActiveKeyFramework(k);
									}}
									className="mb-3"
								>
									{projectFrameworks
										.sort((a, b) => b.id - a.id)
										.map((framework, frameworkIndex) => {
											//console.log('framework:', framework);
											const totalPerFramework = framework.economicFrameworkValues.reduce((tot, elem) => {
												return tot + Number(elem.value);
											}, 0);

											return (
												<Tab
													key={`key-tab-framework-${framework.id}`}
													eventKey={`framework-${framework.id}`}
													title={
														<span>
															Variante {framework.number}{' '}
															{framework.active && isEnabledToView && (
																<span variant="link" className="ms-1 align-text-top" onClick={() => handleDeleteVariant()}>
																	<IcoMinusCircled className="red-color ico-small plus" />
																</span>
															)}
														</span>
													}
												>
													{isEnabledToView && (
														<>
															<DataHeader
																showCol1={false}
																data={[
																	{
																		name: 'Totale per variante',
																		value: (totalPerFramework ? formatToDecimals(totalPerFramework) : 0) + ' €',
																	},
																]}
																childrenButton={
																	isEnabledToView && (
																		<>
																			<Row className="flex-column gap-2">
																				<Col className="pe-0">
																					<Row className="align-items-center">
																						<Col className="me-auto">
																							<OverlayTrigger placement="top" overlay={<Tooltip>Rimuovi/aggiungi mese all'inizio</Tooltip>}>
																								<span>Mese Inizio</span>
																							</OverlayTrigger>
																						</Col>

																						<Col className="col-auto px-0">
																							<Button
																								variant="link"
																								disabled={!framework.active}
																								onClick={() => deleteMonthStart(frameworkIndex, framework.id)}
																							>
																								<IcoMinusCircled className="red-color ico-small plus" />
																							</Button>
																						</Col>
																						<Col className="col-auto ps-1">
																							<Button
																								variant="link"
																								disabled={!framework.active}
																								onClick={() => addMonthStart(frameworkIndex, framework.id)}
																							>
																								<IcoPlus className="second-color ico-small plus" />
																							</Button>
																						</Col>
																					</Row>
																				</Col>

																				<Col className="pe-0">
																					<Row className="align-items-center">
																						<Col className="me-auto">
																							<OverlayTrigger placement="top" overlay={<Tooltip>Rimuovi/aggiungi mese alla fine</Tooltip>}>
																								<span>Mese Fine</span>
																							</OverlayTrigger>
																						</Col>
																						<Col className="col-auto px-0">
																							<Button
																								variant="link"
																								disabled={!framework.active}
																								onClick={() => deleteMonthEnd(frameworkIndex, framework.id)}
																							>
																								<IcoMinusCircled className="red-color ico-small plus" />
																							</Button>
																						</Col>
																						<Col className="col-auto ps-1">
																							<Button
																								variant="link"
																								disabled={!framework.active}
																								onClick={() => addMonthEnd(frameworkIndex, framework.id)}
																							>
																								<IcoPlus className="second-color ico-small plus" />
																							</Button>
																						</Col>
																					</Row>
																				</Col>
																			</Row>
																			{/* <ButtonToolbar className='justify-content-end'>
                                                <InputGroup className='align-items-start mt-3 mt-md-0 ms-2'>
                                                   <OverlayTrigger placement='top' overlay={<Tooltip>Rimuovi/aggiungi mese all'inizio</Tooltip>}>
                                                      <InputGroup.Text className="bg-transparent">Mese Inizio</InputGroup.Text>
                                                   </OverlayTrigger>
                                                   
                                                   <Button variant="light" onClick={() => deleteMonthStart(frameworkIndex, framework.id)}><IcoMinusCircled className='red-color ico-small plus'/></Button>
                                                   <Button variant="light" className="pe-0" onClick={() => addMonthStart(frameworkIndex, framework.id)}><IcoPlus className="second-color ico-small plus"/></Button>
                                                </InputGroup>

                                                <InputGroup className='align-items-start mt-3 mt-md-0 ms-2'>
                                                   <OverlayTrigger placement='top' overlay={<Tooltip>Rimuovi/aggiungi mese alla fine</Tooltip>}>
                                                      <InputGroup.Text className="bg-transparent">Mese Fine</InputGroup.Text>
                                                   </OverlayTrigger>

                                                   <Button variant="light" onClick={() => deleteMonthEnd(frameworkIndex, framework.id)}><IcoMinusCircled className='red-color ico-small plus'/></Button>
                                                   <Button variant="light" className="pe-0" onClick={() => addMonthEnd(frameworkIndex, framework.id)}><IcoPlus className="second-color ico-small plus"/></Button>
                                                </InputGroup>
                                             </ButtonToolbar> */}
																		</>
																	)
																}
															/>

															{/* <Row className='mt-3 mb-4 justify-content-center'>
                                    <Col className='col-auto'><Button variant="success" onClick={() => addMonthStart(frameworkIndex, framework.id)}>Aggiungi mese all'inizio</Button> </Col>
                                    <Col className='col-auto'><Button variant="success" onClick={() => addMonthEnd(frameworkIndex, framework.id)}>Aggiungi mese alla fine</Button> </Col>
                                    <Col className='col-auto'><Button variant="danger" onClick={() => deleteMonthStart(frameworkIndex, framework.id)}>Elimina mese all'inizio</Button> </Col>
                                    <Col className='col-auto'><Button variant="danger" onClick={() => deleteMonthEnd(frameworkIndex, framework.id)}>Elimina mese alla fine</Button></Col>
                                 </Row> */}
														</>
													)}

													<Tabs
														id={`sub-tab-framework-${framework.id}`}
														defaultActiveKey={`stream-tab-${streams?.[0]?.id}`}
														className="mb-3"
													>
														{streams.map((stream) => (
															<Tab
																key={`key-tab-stream-${stream.id}`}
																eventKey={`stream-tab-${stream.id}`}
																title={`${stream.streamId} - ${stream.name}`}
															>
																{projectNoticeExpenseItems.map((expense, index) => {
																	return (
																		<Fragment key={index}>
																			<Row>
																				<Col className="col-2 fw-bold py-3">{expense.name}</Col>
																				<Col className="p-0">
																					{projectExpenseItemsForAmounts
																						.sort((a, b) => a.position - b.position)
																						.map((projectExpense, valueIndex) => {
																							const arrayOfYears = framework.economicFrameworkValues
																								.filter((econFram) => econFram.projectExpenseItemId === projectExpense.id)
																								.filter((value, index, array) => array.findIndex((v) => v.year === value.year) === index)
																								.map((el) => el.year);
																							const totalPerExpenseItem = framework.economicFrameworkValues
																								.filter(
																									(econFram) =>
																										econFram.projectExpenseItemId === projectExpense.id &&
																										econFram.projectStreamId === stream.id,
																								)
																								.reduce((tot, elem) => {
																									return tot + Number(elem.value);
																								}, 0);
																							//console.log('projectExpense', projectExpense);
																							//console.log('framework.economicFrameworkValues', framework.economicFrameworkValues);

																							return projectExpense.projectNoticeExpenseItemId === expense.id ? (
																								<Row
																									key={projectExpense.id}
																									style={{
																										borderTop: valueIndex > 0 ? '1px solid rgb(222, 226, 230)' : 'none',
																									}}
																								>
																									<Col className="col-3 px-3 pt-4 align-top">{projectExpense.name}</Col>
																									<Col className="px-3 pt-3">
																										<Row>
																											<Col>
																												<Tabs>
																													{arrayOfYears
																														.sort((a, b) => a - b)
																														.map((year) => {
																															const filteredValues = framework.economicFrameworkValues.filter(
																																(econFrameValue) =>
																																	econFrameValue.year === year &&
																																	econFrameValue.projectExpenseItemId === projectExpense.id &&
																																	econFrameValue.projectStreamId === stream.id &&
																																	econFrameValue.projectEconomicFrameworkId === framework.id,
																															);
																															const totalPerMonths = filteredValues.reduce((tot, elem) => {
																																return tot + Number(elem.value);
																															}, 0);
																															//let showMonths = false;
																															//console.log('showMonths', showMonths);

																															return (
																																<Tab
																																	key={projectExpense.id + ' ' + year}
																																	eventKey={projectExpense.id + ' ' + year}
																																	title={year}
																																>
																																	<SwitchShowMonths
																																		filteredValues={filteredValues}
																																		months={months()}
																																		handleTargetAmountChange={handleTargetAmountChange}
																																		frameworkIndex={frameworkIndex}
																																		localLang={localLang}
																																		totalPerMonths={totalPerMonths}
																																		isActive={framework.active}
																																		isEnabledToView={isEnabledToView}
																																	/>
																																</Tab>
																															);
																														})}

																													<Tab
																														eventKey={projectExpense.id + '-total'}
																														title={'Totale'}
																														className="py-4 px-3"
																														style={{ minWidth: '25rem' }}
																													>
																														<table>
																															<tbody>
																																{arrayOfYears.map((year) => {
																																	const totalPerYear = framework.economicFrameworkValues
																																		.filter(
																																			(econFram) =>
																																				econFram.projectExpenseItemId === projectExpense.id &&
																																				econFram.year === year &&
																																				econFram.projectStreamId === stream.id,
																																		)
																																		.reduce((tot, elem) => {
																																			return tot + Number(elem.value);
																																		}, 0);

																																	return (
																																		<tr key={year + '-' + projectExpense.id}>
																																			<td className="py-2 fw-bold">{year}</td>
																																			<td className="py-2 ps-5 text-end">
																																				{formatToDecimals(totalPerYear) + ' €'}
																																			</td>
																																		</tr>
																																	);
																																})}

																																<tr>
																																	<td className="py-2 fw-bold text-uppercase">TOTALE</td>
																																	<td className="py-2 ps-5 text-end">
																																		{formatToDecimals(totalPerExpenseItem) + ' €'}
																																	</td>
																																</tr>
																															</tbody>
																														</table>
																													</Tab>
																												</Tabs>
																											</Col>
																										</Row>
																									</Col>
																								</Row>
																							) : (
																								''
																							);
																						})}
																				</Col>
																			</Row>
																		</Fragment>
																	);
																})}
																{isEnabledToView && (
																	<Row className="mt-5 text-center">
																		<Col>
																			<Button
																				onClick={() => updateValues(framework.id)}
																				disabled={projectFrameworksAmount.length === 0 ? true : false}
																			>
																				<OETrans code="common.save" fallback={'Salva'} />
																			</Button>
																		</Col>
																	</Row>
																)}
															</Tab>
														))}

														<Tab eventKey={`document-tab-${framework.id}`} title="Documenti">
															{framework.id && (
																<UploadDocument
																	entity="projectEconomicFrameworks"
																	entityId={framework.id}
																	key={`key-document-tab-${framework.id}`}
																/>
															)}
														</Tab>
													</Tabs>
												</Tab>
											);
										})}

									{isEnabledToView && (
										<Tab
											eventKey={'add-target'}
											title={
												<OverlayTrigger placement="top" overlay={<Tooltip>Aggiungi quadro economico</Tooltip>}>
													<IcoPlus className="second-color ico-small plus" />
												</OverlayTrigger>
											}
										></Tab>
									)}
								</Tabs>
							</Row>

							<DialogConfirmation
								title={translate('common.planning.deleteeconomicframework', 'Elimina quadro economico')}
								content={translate('common.warning.delete', 'Sei sicuro di voler eliminare questo elemento?')}
								confirmLabel={translate('common.delete', 'Elimina')}
								cancelLabel={translate('common.cancel', 'Annulla')}
								onConfirm={() => removeVariant()}
								onCancel={() => setShowDeleteModal(false)}
								show={showDeleteModal}
								onHide={() => setShowDeleteModal(false)}
							/>
						</div>
					</>
				)}
			</Fragment>
		);
	};

	return (
		<>
			{!projectFrameworks ? <Loading fixedHeight="50vh" /> : <AmountsTab />}

			{sbShow}
		</>
	);
};
export default BudgetStreams;
